'use client'

import { Container, DesktopOnly, FlexContainer, GridArea, GridContainer, LineSeparator } from '@news12/storybook-news'

import React, { useEffect, useMemo } from 'react'

import StoriesSection from '../../components/StoriesSection/StoriesSection'
import Hero from '../../components/Story/Hero'
import StoryTitle from '../../components/Story/StoryTitle'
import useBelowBreakpoint from '../../hooks/useBelowBreakpoint'
import useStoryPageTracking from '../../hooks/useStoryPageTracking'
import { StoryPreview, TopStory } from '../../types'
import extractVideoUrl from '../../utils/extractVideoUrl'
import AdUnit from '../Ads/AdUnit'
import MinutelyWidget from '../Ads/MinutelyWidget'
import Taboola from '../Ads/Taboola'
import { useAdsContext } from '../AdsProvider'
import DvdPreRollAdManager from '../DvdPreRollAdManager'
import LazyLoad from '../utilities/lazyLoad'

import { useTheme } from 'styled-components'

const gridTemplateAreasForMobile = `
  'story-title'
  'story-hero'
`

const gridTemplateAreasForDesktop = `
  'story-hero sidebar'
  'story-title sidebar'
  '. sidebar'
`

type ShortStoryPageProps = {
  story: TopStory
  moreStories: StoryPreview[]
}

const ShortStoryPage = ({ story, moreStories }: ShortStoryPageProps) => {
  useStoryPageTracking(story)
  const { changeCategoriesNames, resetCategoriesNames } = useAdsContext()

  const categoriesNames = useMemo(() => story.categories.map(({ name }) => `category/${name?.toLowerCase()}`), [story])

  const isTablet = useBelowBreakpoint('lg')
  const theme = useTheme()

  const isAdsEnabled = story.withAds
  const isTaboolaEnabled = story.withTaboola && story.withAds
  const isPreRollEnabled = story.withPreRoll && story.withAds

  const heroExists = extractVideoUrl(story?.videoFile?.videoUrls) || story?.heroImage

  useEffect(() => {
    changeCategoriesNames(categoriesNames)

    return () => resetCategoriesNames()
  }, [categoriesNames])

  const sidebarAdUnitSizes = useMemo(
    () => ({
      height: 'auto',
      minHeight: '250px'
    }),
    []
  )

  return (
    <>
      <DvdPreRollAdManager isEnabled={story.withDvdPreRoll} />
      {isAdsEnabled && (
        <Container
          fullWidth
          backgroundColor={isTablet ? 'transparent' : `${theme.colors.mist}80`}
          margin={['1rem 0', '0 0 1.5rem']}
          padding={{ default: 0, lg: '0.5rem 0' }}>
          <FlexContainer justifyContent='center'>
            <AdUnit
              adUnitTemplate='leaderboard'
              contentfulCategories={categoriesNames}
              id='ad-banner-top'
              type='leaderboardRedesign'
            />
          </FlexContainer>
        </Container>
      )}
      <Container padding={{ default: '0 1rem', lg: '0 3.75rem' }}>
        <GridContainer
          gridTemplateAreas={{ default: gridTemplateAreasForMobile, lg: gridTemplateAreasForDesktop }}
          gridTemplateColumns={{
            default: 'minmax(0, 1fr)',
            lg: isAdsEnabled ? 'minmax(0, 1fr) 407px' : 'minmax(0, 1fr)'
          }}>
          <GridArea
            className='story-title'
            gridArea='story-title'
            margin={{ default: !heroExists ? '0 0 2rem' : '0', lg: '0' }}>
            <StoryTitle story={story} />
          </GridArea>
          {heroExists && (
            <GridArea gridArea='story-hero' margin={{ default: '1rem 0 2rem', lg: '0 0 1rem' }}>
              <Hero story={story} withAds={isPreRollEnabled} />
            </GridArea>
          )}
          {isAdsEnabled && !isTablet && (
            <GridArea
              gridArea='sidebar'
              margin={{ default: '0 0 2rem', lg: '0 0 0 2.5rem' }}
              display={{ default: 'none', lg: 'block' }}>
              <FlexContainer margin='0 0 2.5rem'>
                <AdUnit
                  adUnitTemplate='rectangleVertical'
                  adUnitSizes={sidebarAdUnitSizes}
                  contentfulCategories={categoriesNames}
                  id='ad-banner-sidebar-top'
                  type='sidebar1'
                />
              </FlexContainer>
            </GridArea>
          )}
        </GridContainer>
        {isAdsEnabled && !isTablet && (
          <>
            <LineSeparator margin='1.5rem auto' />
            <FlexContainer justifyContent='center'>
              <AdUnit
                adUnitTemplate='leaderboard'
                contentfulCategories={categoriesNames}
                id='ad-banner-end-story'
                type='leaderboardDynamic'
              />
            </FlexContainer>
          </>
        )}
        {Boolean(moreStories?.length) && (
          <>
            <DesktopOnly breakpoint={theme.breakpoints.lg}>
              <LineSeparator margin='1.5rem auto' />
            </DesktopOnly>
            <Container
              padding={{ default: '3.5rem 0 1.5rem', lg: '0' }}
              margin={{ default: '-3rem -1rem 0', lg: '0' }}
              backgroundColor={isTablet ? `${theme.colors.mist}80` : 'transparent'}>
              <StoriesSection
                adUnitCategories={categoriesNames}
                category='More Stories'
                shouldShowAd={!isTablet}
                shouldShowArrows={!isTablet}
                stories={moreStories}
                fullWidthBreakpoint='lg'
              />
            </Container>
          </>
        )}
        {isAdsEnabled && (
          <>
            <DesktopOnly breakpoint={theme.breakpoints.lg}>
              <LineSeparator margin='1.5rem auto' />
            </DesktopOnly>
            <FlexContainer
              alignItems='center'
              direction='column'
              gap='1.25rem'
              padding={{ default: '1.25rem 0 0', lg: 0 }}>
              {isTablet && (
                <AdUnit
                  adUnitTemplate='leaderboard'
                  contentfulCategories={categoriesNames}
                  id='ad-banner-end-story'
                  type='leaderboardDynamic'
                />
              )}
              <MinutelyWidget />
              {isTaboolaEnabled && (
                <LazyLoad>
                  <Taboola />
                </LazyLoad>
              )}
            </FlexContainer>
          </>
        )}
      </Container>
    </>
  )
}

export default ShortStoryPage
